import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Check } from 'lucide-react';
import { api } from '@/lib/api';
import type { GrSubscriptionTier } from '@/lib/api';
import { formatCreditsWithDollar } from '@/lib/format-credits';
import { useSubscription, useSubscriptionTiers } from '../hooks';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { TierPricingCard } from '../components/TierPricingCard';
import { ReturnToArcheButton } from '../components/ReturnToArcheButton';

/**
 * Lit Pass purchase page. A Lit Pass is bought with Fias credits only; the
 * tier prices come from the server (`GET /tiers`) and are shown in credits with
 * their dollar equivalent. The balance shown is the Fias bucket, because that
 * is the only bucket an in-arche purchase may draw from. A game may open this
 * page in a new tab with `?return=<arche id>`, which puts a "Back to <the
 * game>" button at the top.
 */
export function SubscriptionPage() {
  const {
    subscription,
    loading: subLoading,
    error: subError,
    refresh: refreshSub,
  } = useSubscription();
  const { tiers, loading: tiersLoading, error: tiersError } = useSubscriptionTiers();
  const [purchasing, setPurchasing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [spendableCredits, setSpendableCredits] = useState<number | null>(null);
  // One idempotency key per purchase INTENT, not per click: it is minted when a
  // purchase starts and cleared only when the server confirms it, so a retry
  // after a lost response re-sends the same key and gets a 409 instead of a
  // second charge. A different tier is a different intent and gets a new key.
  const purchaseIntent = useRef<{ tier: GrSubscriptionTier; key: string } | null>(null);

  const error = subError || tiersError || actionError;

  // The balance is a courtesy beside the prices; a failed read leaves it off.
  // Only the Fias bucket is spendable here (an IAP purchase never draws on a
  // subscription allocation), so that is the number shown.
  const loadSpendableCredits = useCallback(async () => {
    try {
      const b = await api.getCreditBalance();
      setSpendableCredits(b.buckets.fias);
    } catch {
      // Leave the previous value (or nothing) in place.
    }
  }, []);

  useEffect(() => {
    void loadSpendableCredits();
  }, [loadSpendableCredits]);

  const handlePurchase = async (tier: GrSubscriptionTier) => {
    setPurchasing(true);
    setActionError(null);
    setSuccessMessage(null);
    if (purchaseIntent.current?.tier !== tier) {
      purchaseIntent.current = { tier, key: crypto.randomUUID() };
    }
    try {
      const result = await api.purchaseGameRewardsSubscription(tier, purchaseIntent.current.key);
      purchaseIntent.current = null;
      const displayName = tiers.find((t) => t.tier === tier)?.displayName ?? tier;
      setSuccessMessage(
        `${displayName} Lit Pass activated for ${formatCreditsWithDollar(result.priceCredits)} credits.`,
      );
      refreshSub();
      void loadSpendableCredits();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to purchase Lit Pass');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <ReturnToArcheButton />
      <div>
        <Link
          to="/a/arche_game_rewards"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <h1 className="text-2xl font-semibold">Subscription</h1>
        {spendableCredits !== null && (
          <p className="text-sm text-muted-foreground mt-1">
            Spendable Fias credits: {formatCreditsWithDollar(spendableCredits)}
          </p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 rounded-md border border-green-500 bg-green-500/10 p-3">
          <Check className="h-4 w-4 text-green-600" />
          <p className="text-sm">{successMessage}</p>
        </div>
      )}

      <div>
        <h2 className="text-lg font-medium mb-3">Current Subscription</h2>
        {subLoading ? (
          <Skeleton className="h-40 w-full max-w-sm rounded-lg" />
        ) : (
          <div className="max-w-sm">
            <SubscriptionCard subscription={subscription} />
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-medium mb-3">Available Tiers</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Lit Passes are paid with Fias credits. Buying a new pass while one is active converts the
          unused days into bonus days on the new pass.
        </p>
        {tiersLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-lg" />
            ))}
          </div>
        ) : tiers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tiers available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <TierPricingCard
                key={tier.tierId}
                tier={tier}
                currentTier={subscription?.tier ?? null}
                onPurchase={handlePurchase}
                purchasing={purchasing}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
